import React from 'react';
import VoiceAgentDetailsFormFields from './VoiceAgentDetailsFormFields.tsx';
import VoiceAgentVoiceSettingsFormFields from './VoiceAgentVoiceSettingsFormFields.tsx';
import VoiceAgentToolsFormFields from './VoiceAgentToolsFormFields.tsx';
import VoiceAgentPromptFormFields from './VoiceAgentPromptFormFields.tsx';
import VoiceAgentKnowledgeFormFields from './VoiceAgentKnowledgeFormFields.tsx';
import VoiceAgentSummaryFormFields from './VoiceAgentSummaryFormFields.tsx';
import VoiceAgentNotificationsFormFields from './VoiceAgentNotificationsFormFields.tsx';
import {
    CALLER_VERIFICATION_ATTEMPTS_DEFAULT,
    CALLER_VERIFICATION_FIELD_CUSTOMER_NUMBER,
    DEFAULT_ENABLED_TOOLS,
    MAX_CALL_DURATION_DEFAULT,
    PROMPT_DEFAULT_APPROACH,
    PROMPT_DEFAULT_FORMALITY,
    PromptApproach,
    PromptFormality,
    PromptGuardrail,
    VOICE_MODEL_CONVERSATIONAL,
    VOICE_SPEED_DEFAULT,
} from '../constants.ts';
import { isEditablePromptValid } from '../utils/editablePrompt.ts';
import { isValidRecipientList } from '../utils/recipients.ts';

export const VOICE_AGENT_FORM_STEPS = [
    'details',
    'voice',
    'tools',
    'notifications',
    'prompt',
    'knowledge',
    'summary',
] as const;

export interface VoiceAgentDefaultChannels {
    orderChannelId: string;
    rfqChannelId: string;
}

export interface VoiceAgentDocumentFieldSelection {
    headerFields: string[];
    lineItemFields: string[];
}

export interface VoiceAgentDocumentFields {
    order: VoiceAgentDocumentFieldSelection | null;
    rfq: VoiceAgentDocumentFieldSelection | null;
}

// One phase of the conversation flow; a phase is in the list or it is not (ENG-11790)
export interface PromptPhase {
    goal: string;
    entry: string;
    procedure: string;
    response: string;
    nextStep: string;
}

// The customer-editable part of the prompt, as the backend's VoiceAgentEditablePrompt exposes it
export interface VoiceAgentEditablePromptFormData {
    role: string;
    guardrails: PromptGuardrail[];
    formality: PromptFormality;
    approach: PromptApproach;
    phases: PromptPhase[];
    generalRemarks: string;
}

export const EMPTY_PROMPT_PHASE: PromptPhase = { goal: '', entry: '', procedure: '', response: '', nextStep: '' };

export const EDITABLE_PROMPT_DEFAULT_VALUES: VoiceAgentEditablePromptFormData = {
    role: '',
    guardrails: [],
    formality: PROMPT_DEFAULT_FORMALITY,
    approach: PROMPT_DEFAULT_APPROACH,
    phases: [],
    generalRemarks: '',
};

export interface VoiceAgentFormData {
    name: string;
    teamId?: string;
    language: string;
    introSpeech: string;
    introSpeechPersonalized: string;
    consentQuestion: string;
    phoneNumber: string;
    transferPhoneNumber: string;
    fallbackEmail: string;
    sendEmailAllowedDomains: string[];
    callerIdentificationLookupDefinitionId: string;
    orderHistoryLookupDefinitionId: string;
    orderLookupDefinitionId: string;
    articleLookupDefinitionId: string;
    erpToolProviderKey: string;
    erpToolProviderIntegrationId: string;
    requireIdentifiedCaller: boolean;
    callerRejectionMessage: string;
    requireCallerVerification: boolean;
    callerVerificationField: string;
    callerVerificationExtraDataKey: string;
    callerVerificationQuestion: string;
    callerVerificationMaxAttempts: string;
    callerVerificationFailedMessage: string;
    backgroundVoiceDetection: boolean;
    voiceModel: string;
    voiceSpeed: number;
    maxCallDurationSeconds: number;
    forwardOnConsentDenial: boolean;
    webCallRequestsEnabled: boolean;
    webCallAllowedPrefixes: string;
    webCallDailyCap: string;
    webCallCooldownSeconds: string;
    webCallAskConsentQuestion: boolean;
    calendlyMeetingLink: string;
    calendlySummaryQuestion: string;
    calendlyApiToken: string;
    calendlyApiTokenSet: boolean;
    summaryEmailEnabled: boolean;
    summaryEmailRecipients: string[];
    summaryTeamsEnabled: boolean;
    summaryTeamsTeamId: string;
    summaryTeamsTeamName: string;
    summaryTeamsChannelId: string;
    summaryTeamsChannelName: string;
    enabledTools: string[];
    defaultChannels: VoiceAgentDefaultChannels | null;
    documentFields: VoiceAgentDocumentFields;
    editablePrompt: VoiceAgentEditablePromptFormData;
    // read-only: whether the agent already runs on the editable prompt (switched by Workist in the admin)
    editablePromptEnabled: boolean;
    files: (File | any)[];
}

export const VOICE_AGENT_FORM_DEFAULT_VALUES: VoiceAgentFormData = {
    name: '',
    teamId: undefined,
    language: 'de',
    introSpeech: '',
    introSpeechPersonalized: '',
    consentQuestion: '',
    phoneNumber: '',
    transferPhoneNumber: '',
    fallbackEmail: '',
    sendEmailAllowedDomains: [],
    callerIdentificationLookupDefinitionId: '',
    orderHistoryLookupDefinitionId: '',
    orderLookupDefinitionId: '',
    articleLookupDefinitionId: '',
    erpToolProviderKey: '',
    erpToolProviderIntegrationId: '',
    requireIdentifiedCaller: false,
    callerRejectionMessage: '',
    requireCallerVerification: false,
    callerVerificationField: CALLER_VERIFICATION_FIELD_CUSTOMER_NUMBER,
    callerVerificationExtraDataKey: '',
    callerVerificationQuestion: '',
    callerVerificationMaxAttempts: String(CALLER_VERIFICATION_ATTEMPTS_DEFAULT),
    callerVerificationFailedMessage: '',
    backgroundVoiceDetection: true,
    voiceModel: VOICE_MODEL_CONVERSATIONAL,
    voiceSpeed: VOICE_SPEED_DEFAULT,
    maxCallDurationSeconds: MAX_CALL_DURATION_DEFAULT,
    forwardOnConsentDenial: false,
    webCallRequestsEnabled: false,
    webCallAllowedPrefixes: '+49, +43, +41',
    webCallDailyCap: '50',
    webCallCooldownSeconds: '3600',
    webCallAskConsentQuestion: false,
    calendlyMeetingLink: '',
    calendlySummaryQuestion: '',
    calendlyApiToken: '',
    calendlyApiTokenSet: false,
    summaryEmailEnabled: false,
    summaryEmailRecipients: [],
    summaryTeamsEnabled: false,
    summaryTeamsTeamId: '',
    summaryTeamsTeamName: '',
    summaryTeamsChannelId: '',
    summaryTeamsChannelName: '',
    enabledTools: DEFAULT_ENABLED_TOOLS,
    defaultChannels: null,
    documentFields: { order: null, rfq: null },
    editablePrompt: { ...EDITABLE_PROMPT_DEFAULT_VALUES },
    editablePromptEnabled: true,
    files: [],
};

export interface VoiceAgentConfigStep {
    title: string;
    description: string;
    component: React.ComponentType<{ form: any; teamOptions?: any[]; isNewAgent?: boolean }>;
    isValid?: (form: any) => boolean;
    isSkippable?: boolean;
}

export const VOICE_AGENT_CONFIG_STEPS: Record<string, VoiceAgentConfigStep> = {
    details: {
        title: 'form.detailsSectionTitle',
        description: 'form.detailsSectionDescription',
        component: ({ form, teamOptions }) => <VoiceAgentDetailsFormFields form={form} teamOptions={teamOptions} />,
        isValid: (form) => form.watch('name').trim().length > 0,
    },
    voice: {
        title: 'form.voiceSectionTitle',
        description: 'form.voiceSectionDescription',
        component: ({ form }) => <VoiceAgentVoiceSettingsFormFields form={form} />,
        isValid: (form) => form.watch('consentQuestion')?.trim().length > 0,
    },
    tools: {
        title: 'form.toolsSectionTitle',
        description: 'form.toolsSectionDescription',
        component: ({ form }) => <VoiceAgentToolsFormFields form={form} />,
        isValid: (form) => {
            const enabledTools: string[] = form.watch('enabledTools') || [];
            const defaultChannels = form.watch('defaultChannels');
            // create_order/create_rfq need the matching channel configured to be usable.
            if (enabledTools.includes('create_order') && !defaultChannels?.orderChannelId) return false;
            if (enabledTools.includes('create_rfq') && !defaultChannels?.rfqChannelId) return false;
            return true;
        },
    },
    notifications: {
        title: 'form.notificationsSectionTitle',
        description: 'form.notificationsSectionDescription',
        component: ({ form }) => <VoiceAgentNotificationsFormFields form={form} />,
        // an enabled channel needs a target — otherwise summaries would silently go nowhere
        isValid: (form) =>
            isValidRecipientList(form.watch('summaryEmailEnabled'), form.watch('summaryEmailRecipients')) &&
            (!form.watch('summaryTeamsEnabled') || Boolean(form.watch('summaryTeamsChannelId'))),
        isSkippable: true,
    },
    prompt: {
        title: 'form.promptSectionTitle',
        description: 'form.promptSectionDescription',
        component: ({ form, isNewAgent }) => <VoiceAgentPromptFormFields form={form} seedDefaults={isNewAgent} />,
        isValid: (form) => isEditablePromptValid(form.watch('editablePrompt')),
    },
    knowledge: {
        title: 'form.knowledgeSectionTitle',
        description: 'form.knowledgeSectionDescription',
        component: ({ form }) => <VoiceAgentKnowledgeFormFields form={form} />,
        isSkippable: true,
    },
    summary: {
        title: 'form.summarySectionTitle',
        description: 'form.summarySectionDescription',
        component: ({ form }) => <VoiceAgentSummaryFormFields form={form} />,
    },
};
